/**
 * The URL scheme (plan §22). One document, many addresses:
 *
 *     tensorlens.dev/HuggingFaceTB/SmolLM2-135M?rev=<sha>&tensor=…&view=…
 *
 * The repo id lives in the *path*, so a shared link reads like the thing it
 * names, the edge meta-injector has a clean key, and CDN logs group models by
 * `cs_uri_stem` instead of a double-encoded query field. `rev`, `tensor`,
 * `view`, and compare mode's `b`/`brev` stay in the query.
 *
 * Everything here is a pure function over a `(pathname, search)` pair — no
 * `location`, no `history` — so the routing table is unit-testable without a
 * browser and `main.ts` is left with nothing but the two `history.replaceState`
 * calls. That split is deliberate: the parsing is the part that has to be right
 * forever, because URLs are forever.
 *
 * Two rules that look arbitrary and are not:
 *
 *   1. **A repo is exactly two path segments.** Single-segment paths are
 *      reserved for site pages (`/about`, `/colophon`), so no repo can ever
 *      shadow one, and three-plus segments are not repos either. Legacy
 *      single-segment Hub ids (`bert-base-uncased`) are therefore *not*
 *      routable — see `buildUrl` for where they go instead, and
 *      `normalizeRepoId` for why they are still accepted as input.
 *   2. **The legacy `?repo=owner/name` form is parsed forever.** Links shared
 *      before the scheme changed must not break; `main.ts` rewrites them to the
 *      path form with `replaceState`, which is a 301 that costs no request.
 */

/** Everything the app can read out of, or write into, its own URL. */
export interface UrlState {
  /** `owner/name`, decoded. Absent means the empty state, e.g. the root path. */
  readonly repoId?: string;
  /** The commit SHA the view is pinned to (plan §5.2), or a requested revision. */
  readonly rev?: string;
  /**
   * Which model inside the repo, as its directory prefix without the trailing
   * slash (`text_encoder`, `FL2VA/transformer`).
   *
   * A query value rather than a path segment because the path rule is "a repo
   * is exactly two segments" — a third would parse as a different repo id. It
   * sits before `tensor` in the emitted order because it selects the model that
   * `tensor` then indexes into; the reverse reads as a tensor that happens to
   * have a part.
   */
  readonly part?: string;
  readonly tensor?: string;
  /**
   * Compare mode's second repo (plan §4 v2), as `owner/name`.
   *
   * In the *query* and not the path, unlike `repoId`, which is not an
   * inconsistency but the point. A URL has one path and the path names the
   * subject; compare mode has a baseline and a challenger, and they are not
   * symmetric — A is `relF`'s denominator and the thing B is measured against.
   * Putting B in the path too (`/ownerA/nameA/ownerB/nameB`) would read as a
   * fourth-segment repo, collide with the two-segments-is-a-repo rule in
   * `repoIdFromPath`, and imply an equality between the two sides that the
   * statistics do not have. It also keeps `cs_uri_stem` in the CDN logs a clean
   * key for one model (§20) rather than a pair that would fragment the counts.
   *
   * Unlike `repoId` there is no routability constraint here: a legacy
   * single-segment Hub id is perfectly fine as a query value, because nothing
   * about it can shadow a site page.
   */
  readonly compareRepoId?: string;
  /** The commit SHA the compare side is pinned to. Same contract as `rev`. */
  readonly compareRev?: string;
  /**
   * Which model inside B, when B holds several. `part`'s contract, for B.
   *
   * Without it a comparison against a GGUF repo was not expressible at all —
   * `loadCompare` read B with no part, a 25-quantization repo refused, and the
   * comparison people most want from this tool (`Q4_K_M` against `Q8_0`) had no
   * URL and no way to be reached. It is also what makes such a pair
   * *shareable*: both sides pinned, both parts named, the same two files read
   * again later.
   */
  readonly comparePart?: string;
  /** Reserved by the scheme; carried through untouched so a link can't lose it. */
  readonly view?: string;
  /**
   * True when the repo id was read from `?repo=` rather than from the path.
   * `main.ts` uses it as "this URL needs rewriting", nothing more.
   */
  readonly legacy: boolean;
}

/**
 * Is this id addressable as a path, i.e. exactly two non-empty segments?
 *
 * Not the same question as "is this a valid repo id" — `bert-base-uncased` is a
 * real Hub id that this returns false for. Routing is stricter than input.
 */
export function isRoutableRepoId(repoId: string): boolean {
  const parts = repoId.split("/");
  return parts.length === 2 && parts[0] !== "" && parts[1] !== "";
}

/** `undefined` rather than `""`, so an empty param is the same as a missing one. */
function param(params: URLSearchParams, name: string): string | undefined {
  return params.get(name) || undefined;
}

/**
 * Read the app's state out of a URL's path and query.
 *
 * `pathname` and `search` are exactly what `location` hands over: the path is
 * percent-encoded and the search string may or may not carry its leading `?`.
 */
export function parseUrlState(pathname: string, search: string): UrlState {
  const params = new URLSearchParams(search);
  const rev = param(params, "rev");
  const part = param(params, "part");
  const tensor = param(params, "tensor");
  const view = param(params, "view");
  const compareRepoId = param(params, "b");
  const compareRev = param(params, "brev");
  const comparePart = param(params, "bpart");

  const fromPath = repoIdFromPath(pathname);
  if (fromPath !== undefined) {
    // The path wins over a stray `?repo=`: it is the canonical form, and a URL
    // carrying both is already being rewritten by whoever produced it.
    return {
      repoId: fromPath,
      rev,
      part,
      tensor,
      compareRepoId,
      compareRev,
      comparePart,
      view,
      legacy: false,
    };
  }

  const legacyRepo = param(params, "repo");
  if (legacyRepo !== undefined) {
    return {
      repoId: legacyRepo,
      rev,
      part,
      tensor,
      compareRepoId,
      compareRev,
      comparePart,
      view,
      legacy: true,
    };
  }

  return {
    rev,
    part,
    tensor,
    compareRepoId,
    compareRev,
    comparePart,
    view,
    legacy: false,
  };
}

/**
 * The repo id a path names, or `undefined` if the path is not a repo at all.
 *
 * A trailing slash is tolerated (`/owner/name/` is the same model) but is not
 * the canonical form — `buildUrl` never emits one.
 */
function repoIdFromPath(pathname: string): string | undefined {
  const segments = pathname.replace(/^\/+|\/+$/g, "").split("/");
  if (segments.length !== 2 || segments.some((s) => s === "")) return undefined;
  try {
    return segments.map(decodeURIComponent).join("/");
  } catch {
    // A malformed escape (`/owner/%zz`) is not a repo id. Better an empty state
    // than a URIError thrown out of the app's first statement.
    return undefined;
  }
}

/**
 * Render a state back to a root-relative URL — the inverse of `parseUrlState`
 * for any canonical input, which is what makes the rewrite safe to run on every
 * load without slowly mangling the address bar.
 *
 * A repo id that is not routable (a legacy single-segment Hub id such as
 * `bert-base-uncased`) falls back to the legacy `?repo=` form rather than
 * claiming `/bert-base-uncased`, which the scheme reserves for a site page.
 * This is the second reason the legacy form has to keep working: it is not only
 * a compatibility shim for old links, it is where unroutable ids live.
 */
/**
 * The site's own origin, for the one place that needs an absolute URL.
 *
 * `rel="canonical"` is defined against the document's address and a relative
 * value technically resolves — but a canonical that resolves differently
 * depending on which host served it is not doing its job, and this app is served
 * from an apex domain with a `www` alias that 301s to it. Hard-coding the apex
 * is what makes the two spellings agree on one answer.
 */
export const SITE_ORIGIN = "https://tensorlens.dev";

/**
 * The canonical address for a state — the URL a search engine should index and
 * the one every other spelling of this page should defer to.
 *
 * **Strips everything except the repo.** One model is reachable at many URLs:
 * the legacy `?repo=` form, the path form, and the path form times every
 * `rev`/`tensor`/`b`/`brev` combination a visitor can produce by clicking
 * around. Those are the same page in the sense that matters here — the same
 * model, the same prose, the same thing someone searched for — so consolidating
 * them on `/owner/name` is both honest and what keeps a hundred deep links from
 * competing with each other as duplicates.
 *
 * `rev` is dropped deliberately even though it is the most load-bearing
 * parameter in the app. Pinning is what makes a *shared* link reproduce exactly;
 * it is the opposite of what an *indexed* link wants, since a canonical naming
 * one commit would be stale the next time the model is updated, and would
 * accumulate one dead canonical per revision.
 *
 * An unroutable repo id (a legacy single-segment Hub id) has no path form and
 * therefore no canonical of its own; it defers to the site root rather than
 * inventing `/bert-base-uncased`, which the scheme reserves for a site page.
 */
export function canonicalUrl(state: UrlState): string {
  if (state.repoId === undefined || !isRoutableRepoId(state.repoId)) {
    return `${SITE_ORIGIN}/`;
  }
  const path = state.repoId.split("/").map(encodeURIComponent).join("/");
  return `${SITE_ORIGIN}/${path}`;
}

export function buildUrl(state: UrlState): string {
  const params = new URLSearchParams();
  const routable = state.repoId !== undefined && isRoutableRepoId(state.repoId);

  if (state.repoId !== undefined && !routable) params.set("repo", state.repoId);
  if (state.rev) params.set("rev", state.rev);
  if (state.part) params.set("part", state.part);
  if (state.tensor) params.set("tensor", state.tensor);
  // `b` before `brev` and both before `view`, so the emitted order is stable and
  // `buildUrl(parseUrlState(x)) === x` holds for canonical x — the round-trip
  // test that keeps the address bar from drifting one rewrite at a time.
  if (state.compareRepoId) params.set("b", state.compareRepoId);
  if (state.compareRev) params.set("brev", state.compareRev);
  // After `brev`, mirroring `part` sitting after `rev` on A's side: the part
  // selects the model that the revision pins, so it reads in that order.
  if (state.comparePart) params.set("bpart", state.comparePart);
  if (state.view) params.set("view", state.view);

  const path =
    state.repoId !== undefined && routable
      ? `/${state.repoId.split("/").map(encodeURIComponent).join("/")}`
      : "/";

  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
